import React from 'react';
import { Helmet } from 'react-helmet-async';
import { localeForCountry } from '../utils/locale';

const SITE_NAME = 'lovinahome';
const SITE_URL = 'https://lovinahome.com';
const DEFAULT_DESCRIPTION =
  'Professional property intelligence for Ireland and the UK. Verified listings, true cost transparency, AI-powered investment analysis and negotiation strategy. Buy, rent and research homes with confidence.';
const DEFAULT_IMAGE = `${SITE_URL}/api/og/default.png`;

// Map raw country strings to ISO-3166-1 alpha-2 codes for schema.org and
// `og:locale`. Anything UK-shaped → GB / en_GB. Otherwise → IE / en_IE.
const UK_COUNTRY_NAMES = new Set([
  'UK', 'GB', 'United Kingdom', 'Great Britain',
  'England', 'Scotland', 'Wales', 'Northern Ireland',
]);
const countryIsUk = (c) => !!c && UK_COUNTRY_NAMES.has(String(c));
const addressCountryCode = (c) => (countryIsUk(c) ? 'GB' : 'IE');
const ogLocaleFor = (c) => (countryIsUk(c) ? 'en_GB' : 'en_IE');

/**
 * Reusable SEO component. Pass props per page.
 *
 *   <SEO
 *     title="Stunning 3 Bed Semi-Detached"
 *     description="A beautifully presented…"
 *     image="https://…/cover.jpg"
 *     path="/property/abc123"
 *     country="United Kingdom"   // optional — flips og:locale to en_GB
 *     jsonLd={{ ... }}
 *   />
 */
const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  path = '/',
  type = 'website',
  jsonLd,
  noIndex = false,
  country,
}) => {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : `${SITE_NAME} — Professional property intelligence`;
  const url = `${SITE_URL}${path}`;
  const ogLocale = ogLocaleFor(country);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        Array.isArray(jsonLd) ? (
          jsonLd.filter(Boolean).map((entry, i) => (
            <script key={i} type="application/ld+json">
              {JSON.stringify(entry)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(jsonLd)}
          </script>
        )
      )}
    </Helmet>
  );
};

/* Helpers to build rich JSON-LD payloads -------------------------------- */

export const buildPropertyJsonLd = (property) => {
  if (!property) return null;
  const id = property._id || property.id;
  const url = `${SITE_URL}/property/${id}`;
  const isRent = property.type === 'rent';
  // schema.org/SingleFamilyResidence vs Apartment — narrow the underlying
  // building type. Google still prefers the broader `RealEstateListing`
  // wrapper for rich-snippet eligibility (price + beds visible in SERPs).
  const buildingType = isRent ? 'Apartment' : 'SingleFamilyResidence';

  // Currency + address country follow the property's market so Google
  // renders the correct symbol in rich results (£ for UK listings, € for IE).
  const L = localeForCountry(property.location?.country);
  const priceCurrency = L.currency;
  const countryCode = addressCountryCode(property.location?.country);

  // Energy rating — surface BER (IE) or EPC (UK) as additionalProperty so
  // Google can show it in rich results.
  const additionalProperties = [];
  if (property.ber) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'BER',
      value: property.ber,
    });
  }
  if (property.epc || property.epc_number) {
    additionalProperties.push({
      '@type': 'PropertyValue',
      name: 'EPC',
      value: property.epc || property.epc_number,
    });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: property.title,
    description: property.description?.slice(0, 500),
    url,
    mainEntityOfPage: url,
    datePosted: property.createdAt || property.created_at,
    image: property.images || [],
    address: {
      '@type': 'PostalAddress',
      streetAddress: property.location?.fullAddress,
      addressLocality: property.location?.area || property.location?.city,
      addressRegion: property.location?.county,
      addressCountry: countryCode,
    },
    geo: property.location?.lat && property.location?.lng
      ? {
          '@type': 'GeoCoordinates',
          latitude: property.location.lat,
          longitude: property.location.lng,
        }
      : undefined,
    // The underlying physical building — nested so Google + Bing can show
    // beds/baths/size in the rich snippet without ambiguity.
    about: {
      '@type': buildingType,
      numberOfRooms: property.bedrooms,
      numberOfBathroomsTotal: property.bathrooms,
      floorSize: property.size
        ? {
            '@type': 'QuantitativeValue',
            value: property.size,
            unitCode: 'MTK',
          }
        : undefined,
      yearBuilt: property.yearBuilt,
      ...(additionalProperties.length ? { additionalProperty: additionalProperties } : {}),
    },
    offers: {
      '@type': 'Offer',
      price: property.price,
      priceCurrency,
      availability: 'https://schema.org/InStock',
      url,
      ...(isRent
        ? {
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: property.price,
              priceCurrency,
              unitCode: 'MON',
            },
          }
        : {}),
    },
  };
};

export const buildOrganizationJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  description: DEFAULT_DESCRIPTION,
  logo: `${SITE_URL}/logo.png`,
  email: 'hello@lovinahome.com',
  sameAs: [],
});

export const buildBreadcrumbJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export default SEO;
